import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Lunaveila cart helpers.
 *
 * Reuses the theme's own cart update pattern: it POSTs to the AJAX cart, then
 * dispatches the same `CartLinesUpdateEvent` that `product-form.js` dispatches.
 * The theme's `cart-drawer-component` (auto open on add) and `cart-items-component`
 * (section morph) already listen for that event, so the drawer opens and every
 * cart section re-renders exactly as it does after a native quantity change.
 * Nothing here reloads the page.
 *
 * Exposed as `window.lvCartAdd` so inline (non-module) scripts in custom-liquid
 * sections (the product page add-to-cart) can call it too.
 */

/**
 * @typedef {{ id: (number|string), quantity?: number }} LvCartLine
 */

/**
 * Collect the section ids of every cart-items-component on the page so the AJAX
 * response re-renders them in place (cart drawer, and the /cart page when open).
 * @returns {string[]}
 */
function cartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId && !ids.includes(el.dataset.sectionId)) {
      ids.push(el.dataset.sectionId);
    }
  });
  return ids;
}

/**
 * Add one or more lines to the cart via AJAX and drive the theme's cart UI.
 * @param {LvCartLine | LvCartLine[]} lines
 * @returns {Promise<void>}
 */
export async function lvCartAdd(lines) {
  const items = (Array.isArray(lines) ? lines : [lines])
    .filter((line) => line && line.id != null)
    .map((line) => ({ id: line.id, quantity: Number(line.quantity) || 1 }));

  if (items.length === 0) return;

  const itemCount = items.reduce((sum, line) => sum + line.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  // Dispatch before the request (as product-form.js does) so listeners can prepare.
  document.documentElement.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  /** @type {any} */
  let addResponse;
  try {
    addResponse = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items,
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    }).then((response) => response.json());
  } catch (error) {
    document.dispatchEvent(
      new CartErrorEvent({
        error: (error && error.message) || 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
    deferred.reject(error);
    throw error;
  }

  // Shopify only sets `status` on the response when the add failed (e.g. sold out).
  if (addResponse.status) {
    const message = addResponse.description || addResponse.message || 'Add to cart failed';
    document.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code: 'INVALID',
        detail: { description: addResponse.description, errors: addResponse.errors },
      })
    );
    // Resolve (not reject) so the drawer's auto-open branch treats it as a no-op.
    deferred.resolve({ detail: { didError: true, source: 'lv-cart', itemCount } });
    throw new Error(message);
  }

  /** @type {any} */
  let cart;
  try {
    cart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
  } catch (_error) {
    // Add succeeded but the cart refresh failed. Still resolve with the rendered
    // sections so the cart-items-component can morph from the add response.
    deferred.resolve({
      detail: { source: 'lv-cart', itemCount, sections: addResponse.sections, didError: false },
    });
    return;
  }

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      source: 'lv-cart',
      itemCount,
      sections: addResponse.sections,
      didError: false,
    },
  });
}

// -- Upsell button (delegated so it survives cart section re-renders) --------

const LOADING_ATTR = 'data-lv-loading';

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} loading
 */
function setUpsellLoading(button, loading) {
  if (loading) {
    button.dataset.originalLabel = button.textContent || '';
    button.setAttribute(LOADING_ATTR, 'true');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    const loadingLabel = button.getAttribute('data-loading-label');
    if (loadingLabel) button.textContent = loadingLabel;
  } else {
    button.removeAttribute(LOADING_ATTR);
    button.removeAttribute('aria-busy');
    button.disabled = false;
    if (button.dataset.originalLabel != null) button.textContent = button.dataset.originalLabel;
  }
}

/**
 * @param {Element} button
 * @param {string} message
 */
function showUpsellError(button, message) {
  const container = button.closest('.lv-upsell');
  const target = container ? container.querySelector('[data-lv-upsell-error]') : null;
  if (target instanceof HTMLElement) {
    target.textContent = message;
    target.hidden = false;
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-lv-upsell-add]') : null;
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  if (button.hasAttribute(LOADING_ATTR)) return;

  const variantId = button.getAttribute('data-variant-id');
  if (!variantId) return;
  const quantity = parseInt(button.getAttribute('data-quantity') || '1', 10) || 1;

  const errorEl = button.closest('.lv-upsell')?.querySelector('[data-lv-upsell-error]');
  if (errorEl instanceof HTMLElement) errorEl.hidden = true;

  setUpsellLoading(button, true);

  lvCartAdd([{ id: variantId, quantity }])
    .then(() => {
      // On the /cart page the custom cart section does not morph, so reload to
      // reflect the new quantity, subtotal and upsell tier. In the drawer the
      // cart section morphs and replaces this button, so there is nothing to do.
      if (button.hasAttribute('data-lv-reload')) window.location.reload();
    })
    .catch((error) => {
      setUpsellLoading(button, false);
      showUpsellError(button, (error && error.message) || 'Could not add to cart. Please try again.');
    });
});

// Make available to inline custom-liquid scripts (product page add-to-cart).
window.lvCartAdd = lvCartAdd;
